import React, { useState, useEffect } from 'react';
import { Play, RotateCcw, Save, Trash2, Clock, Smartphone, Code2, Settings, Users, FileText } from 'lucide-react';
import { useSaveGameStore, useDevModeStore } from '../../stores';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { ThemeSettings } from './settings/ThemeSettings';
import type { GameSave } from '@smartphone-tycoon/shared-core';
import packageJson from '../../../package.json' with { type: 'json' };
import Markdown from 'react-markdown';

interface SaveGameScreenProps {
  onNewGame: () => void;
  onLoadGame: (save: GameSave, slotId: number) => void;
  onDesignerStudio: () => void;
  onCommunityHub: () => void;
  onDevMode: () => void;
}

export function MainMenuScreen({ onNewGame, onLoadGame, onDesignerStudio, onCommunityHub, onDevMode }: SaveGameScreenProps) {
  // Direct store access
  const { loading, error, listSaves, loadGame, deleteSave } = useSaveGameStore();
  const devModeEnabled = useDevModeStore(state => state.settings.enabled);
  const toggleDevMode = useDevModeStore(state => state.toggleDevMode);
  const [saves, setSaves] = useState<{ id: number; save: GameSave }[]>([]);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [selectedSave, setSelectedSave] = useState<number | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showChangelog, setShowChangelog] = useState(false);
  const [changelogContent, setChangelogContent] = useState<string>('');

  useEffect(() => {
    const loadSaves = async () => {
      const saveFiles = await listSaves();
      setSaves(saveFiles);
    };
    loadSaves();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // We only want to load once on mount, not when listSaves changes

  useEffect(() => {
    // Load changelog content
    fetch('/CHANGELOG.md')
      .then(response => response.text())
      .then(text => setChangelogContent(text))
      .catch(err => console.error('Failed to load changelog:', err));
  }, []);

  const handleContinue = async () => {
    if (saves.length === 0) return;
    const mostRecent = saves[0];
    const save = await loadGame(mostRecent.id);
    onLoadGame(save, mostRecent.id);
  };

  const handleLoadGame = async (saveId: number) => {
    const save = await loadGame(saveId);
    setShowLoadModal(false);
    onLoadGame(save, saveId);
  };

  const handleDeleteSave = async (saveId: number) => {
    await deleteSave(saveId);
    setSaves(current => current.filter(save => save.id !== saveId));
    setDeleteConfirm(null);
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(date));
  };

  const formatGameDate = (date: { day: number; month: number; year: number }) => {
    return `${date.month}/${date.day}/${date.year}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-green-900 to-teal-900 flex items-center justify-center relative">
      <div className="max-w-lg w-full mx-4">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-theme-menu-title mb-2">Smartphone Tycoon</h1>
        </div>

        <div className="space-y-4">
          <button
            onClick={onNewGame}
            data-testid="new-game-button"
            className="w-full flex items-center justify-between px-6 py-4 bg-theme-bg-content rounded-lg hover:bg-theme-bg-secondary transition-colors group"
          >
            <div className="flex items-center gap-3">
              <RotateCcw className="w-5 h-5 text-theme-accent" />
              <div className="text-left">
                <div className="font-medium text-theme-text-primary">New Game</div>
                <div className="text-sm text-theme-text-muted">Start a fresh company</div>
              </div>
            </div>
            <div className="w-5 h-5 text-theme-text-muted group-hover:text-theme-text-secondary">→</div>
          </button>

          <button
            onClick={handleContinue}
            disabled={saves.length === 0}
            data-testid="continue-button"
            className="w-full flex items-center justify-between px-6 py-4 bg-theme-bg-content rounded-lg hover:bg-theme-bg-secondary transition-colors group disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="flex items-center gap-3">
              <Play className="w-5 h-5 text-theme-accent" />
              <div className="text-left">
                <div className="font-medium text-theme-text-primary">Continue</div>
                {saves.length > 0 ? (
                  <div className="text-sm text-theme-text-muted">
                    {saves[0].save.company.name} - {formatGameDate(saves[0].save.gameDate)}
                  </div>
                ) : (
                  <div className="text-sm text-theme-text-muted">No saved games available</div>
                )}
              </div>
            </div>
            <div className="w-5 h-5 text-theme-text-muted group-hover:text-theme-text-secondary">→</div>
          </button>

          <button
            onClick={() => setShowLoadModal(true)}
            disabled={saves.length === 0}
            data-testid="load-game-button"
            className="w-full flex items-center justify-between px-6 py-4 bg-theme-bg-content rounded-lg hover:bg-theme-bg-secondary transition-colors group disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="flex items-center gap-3">
              <Save className="w-5 h-5 text-theme-accent" />
              <div className="text-left">
                <div className="font-medium text-theme-text-primary">Load Game</div>
                <div className="text-sm text-theme-text-muted">
                  {saves.length} {saves.length === 1 ? 'save' : 'saves'} available
                </div>
              </div>
            </div>
            <div className="w-5 h-5 text-theme-text-muted group-hover:text-theme-text-secondary">→</div>
          </button>

          {/* Designer Studio button - same size as main buttons */}
          <button
            onClick={onDesignerStudio}
            data-testid="designer-studio-button"
            className="w-full flex items-center justify-between px-6 py-4 bg-theme-bg-content rounded-lg hover:bg-theme-bg-secondary transition-colors group"
          >
            <div className="flex items-center gap-3">
              <Smartphone className="w-5 h-5 text-theme-accent" />
              <div className="text-left">
                <div className="font-medium text-theme-text-primary">Designer Studio</div>
                <div className="text-sm text-theme-text-muted">Create and customize smartphone designs</div>
              </div>
            </div>
            <div className="w-5 h-5 text-theme-text-muted group-hover:text-theme-text-secondary">→</div>
          </button>

          {/* Community Hub button - same size as main buttons */}
          <button
            onClick={onCommunityHub}
            className="w-full flex items-center justify-between px-6 py-4 bg-theme-bg-content rounded-lg hover:bg-theme-bg-secondary transition-colors group"
          >
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-theme-accent" />
              <div className="text-left">
                <div className="font-medium text-theme-text-primary">Community Hub</div>
                <div className="text-sm text-theme-text-muted">Share designs and connect with creators</div>
              </div>
            </div>
            <div className="w-5 h-5 text-theme-text-muted group-hover:text-theme-text-secondary">→</div>
          </button>

          {/* Settings button - same size as main buttons */}
          <button
            onClick={() => setShowSettings(true)}
            className="w-full flex items-center justify-between px-6 py-4 bg-theme-bg-content rounded-lg hover:bg-theme-bg-secondary transition-colors group"
            data-testid="settings-button"
          >
            <div className="flex items-center gap-3">
              <Settings className="w-5 h-5 text-theme-accent" />
              <div className="text-left">
                <div className="font-medium text-theme-text-primary">Settings</div>
                <div className="text-sm text-theme-text-muted">Configure game preferences</div>
              </div>
            </div>
            <div className="w-5 h-5 text-theme-text-muted group-hover:text-theme-text-secondary">→</div>
          </button>
        </div>

        {error && (
          <div className="mt-4 p-4 bg-theme-error/10 text-theme-error rounded-lg">
            {error}
          </div>
        )}

        {showLoadModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-theme-bg-content rounded-lg max-w-2xl w-full max-h-[80vh] flex flex-col">
              <div className="p-6 border-b border-theme-border">
                <h2 className="text-xl font-semibold">Load Game</h2>
              </div>

              <div className="flex-1 overflow-auto p-6">
                {loading ? (
                  <div className="text-center py-8 text-theme-text-muted">Loading saves...</div>
                ) : saves.length === 0 ? (
                  <div className="text-center py-8 text-theme-text-muted">No saved games available</div>
                ) : (
                  <div className="space-y-4">
                    {saves.map(({ id, save }) => (
                      <div
                        key={id}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                          selectedSave === id
                            ? 'border-theme-accent bg-theme-accent/10'
                            : 'border-theme-border hover:border-theme-accent/40'
                        }`}
                        onClick={() => setSelectedSave(id)}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-medium text-theme-text-primary">
                              {save.company.name}
                            </h3>
                            <div className="text-sm text-theme-text-muted">
                              CEO: {save.company.ceoName}
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <div className="text-sm font-medium text-theme-text-primary">
                                Game Date: {formatGameDate(save.gameDate)}
                              </div>
                              <div className="text-xs text-theme-text-muted flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {formatDate(save.saveDate)}
                              </div>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteConfirm(id);
                              }}
                              className="p-2 text-theme-text-muted hover:text-theme-error transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        {deleteConfirm === id && (
                          <div className="mt-4 flex items-center justify-end gap-4 border-t pt-4">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteConfirm(null);
                              }}
                              className="px-3 py-1 text-sm text-theme-text-secondary hover:text-theme-text-primary"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteSave(id);
                              }}
                              className="px-3 py-1 text-sm bg-theme-error text-white rounded-md hover:bg-theme-error/80"
                            >
                              Delete Save
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="p-6 border-t border-theme-border bg-theme-bg-secondary rounded-b-lg">
                <div className="flex justify-end gap-4">
                  <button
                    onClick={() => setShowLoadModal(false)}
                    className="px-4 py-2 text-theme-text-secondary hover:text-theme-text-primary"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => selectedSave && handleLoadGame(selectedSave)}
                    disabled={!selectedSave}
                    className="px-4 py-2 bg-theme-accent text-white rounded-lg hover:bg-theme-accent-hover disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Load Selected Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Settings Modal */}
        <Modal 
          isOpen={showSettings} 
          onClose={() => setShowSettings(false)}
          size="md"
          className="max-w-md"
        >
          <div className="space-y-6">
            <div className="flex items-center gap-2 pb-4 border-b border-theme-border">
              <Settings className="w-5 h-5 text-theme-text-secondary" />
              <h2 className="text-lg font-semibold text-theme-text-primary">Settings</h2>
            </div>

            {/* Theme Settings */}
            <ThemeSettings />

            {/* Developer Settings */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-theme-text-secondary border-b border-theme-border pb-2">
                Developer Settings
              </h3>
              
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <label htmlFor="dev-mode-main" className="font-medium text-theme-text-primary">
                    Enable Developer Mode
                  </label>
                  <p className="text-sm text-theme-text-muted mt-1">
                    Show developer tools and debugging information
                  </p>
                  <p className="text-xs text-theme-text-muted mt-1">
                    Press F12 to toggle while in-game
                  </p>
                </div>
                <div className="flex items-center">
                  <input
                    id="dev-mode-main"
                    type="checkbox"
                    checked={devModeEnabled}
                    onChange={() => toggleDevMode()}
                    className="h-4 w-4 text-theme-accent focus:ring-theme-accent border-theme-border rounded"
                  />
                </div>
              </div>

              {devModeEnabled && (
                <div className="mt-2 p-3 bg-theme-warning/10 border border-theme-warning/30 rounded-lg">
                  <p className="text-sm text-theme-warning">
                    <strong>Dev Mode Active:</strong> CEO name will be prefilled as "Mega Man" and company name as "Mega Inc" when starting a new game.
                  </p>
                </div>
              )}
            </div>

            <div className="pt-4 border-t border-theme-border">
              <Button
                onClick={() => setShowSettings(false)}
                variant="secondary"
                className="w-full"
              >
                Close
              </Button>
            </div>
          </div>
        </Modal>
      </div>

      {/* Dev Mode button aligned with version - only show when dev mode is enabled */}
      {devModeEnabled && (
        <div className="fixed bottom-4 left-4">
          <button
            onClick={onDevMode}
            className="flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors text-white border border-white/20"
          >
            <Code2 className="w-4 h-4" />
            <span className="text-sm">Dev Mode</span>
          </button>
        </div>
      )}

      {/* Version Information */}
      <div className="fixed bottom-4 right-4 text-right">
        <div className="text-sm text-white/60">v{packageJson.version}</div>
        <div className="text-xs text-white/40">
          {(packageJson as { buildNumber?: string }).buildNumber || 'dev'}
        </div>
        <div className="text-xs text-white/40">
          {(packageJson as { gitBranch?: string }).gitBranch}
          {(packageJson as { gitCommit?: string }).gitCommit && ` @ ${(packageJson as { gitCommit?: string }).gitCommit}`}
        </div>
{/* TODO: Re-enable when changelog is working
        <button
          onClick={() => setShowChangelog(true)}
          className="text-xs text-white/60 hover:text-white/80 transition-colors mt-1 flex items-center gap-1 ml-auto"
        >
          <FileText className="w-3 h-3" />
          See what's new
        </button>
*/}
        <a
          href="https://forms.gle/Ma6rnBbS4q6d9Fma8"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-white/60 hover:text-white/80 transition-colors mt-1 inline-block"
        >
          Submit feedback
        </a>
      </div>

      {/* Changelog Modal */}
      <Modal
        isOpen={showChangelog}
        onClose={() => setShowChangelog(false)}
        size="lg"
        className="max-w-3xl"
      >
        <div className="h-[80vh] flex flex-col">
          <div className="flex items-center gap-2 pb-4 border-b border-theme-border">
            <FileText className="w-5 h-5 text-theme-text-secondary" />
            <h2 className="text-lg font-semibold text-theme-text-primary">What's New</h2>
          </div>
          
          <div className="flex-1 overflow-auto mt-4 px-6">
            <div className="prose prose-sm max-w-none">
              <Markdown
                components={{
                  h1: ({ children }) => <h1 className="text-2xl font-bold text-theme-text-primary mb-4 mt-6">{children}</h1>,
                  h2: ({ children }) => <h2 className="text-xl font-semibold text-theme-text-primary mb-3 mt-5">{children}</h2>,
                  h3: ({ children }) => <h3 className="text-lg font-medium text-theme-text-secondary mb-2 mt-4">{children}</h3>,
                  p: ({ children }) => <p className="text-theme-text-secondary mb-3 leading-relaxed">{children}</p>,
                  ul: ({ children }) => <ul className="list-disc list-inside text-theme-text-secondary mb-3 ml-4">{children}</ul>,
                  li: ({ children }) => <li className="mb-1">{children}</li>,
                  strong: ({ children }) => <strong className="font-semibold text-theme-text-primary">{children}</strong>,
                  a: ({ children, href }) => (
                    <a href={href} className="text-theme-accent hover:text-theme-accent-hover underline" target="_blank" rel="noopener noreferrer">
                      {children}
                    </a>
                  ),
                  code: ({ children }) => <code className="bg-theme-bg-tertiary px-1 py-0.5 rounded text-sm font-mono text-theme-text-secondary">{children}</code>,
                  pre: ({ children }) => <pre className="bg-theme-bg-tertiary p-3 rounded-lg overflow-x-auto mb-3">{children}</pre>,
                }}
              >
                {changelogContent || 'Loading changelog...'}
              </Markdown>
            </div>
          </div>
          
          <div className="pt-4 border-t border-theme-border">
            <Button
              onClick={() => setShowChangelog(false)}
              variant="secondary"
              className="w-full"
            >
              Close
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}